import t from 'react-translate';
import { useContext, useEffect, useState } from 'react';
import NvIcon from 'shared/components/nv-icon';
import { Button } from 'react-bootstrap';
import { css } from '@emotion/react';
import { doubleSpacing, halfSpacing } from 'styles/global_defaults/scaffolding';
import Spinner from 'shared/components/spinner';
import { config } from '@config/pendo.config.json';
import AiConversationContext, { AIRolePlayStatus, Role, VoiceCardState, VoiceRecordingState } from '../context';
import VideoCard from './video-card';
import useViewedSubmission from '../use-viewed-submission';
import useAiRolePlay from '../../hooks/use-ai-role-play';
import { ClosingReason } from '../stream-ai-response';

const styles = css`
  .video-cards {
    gap: ${doubleSpacing}px;

    > * {
      flex: 1 1 0;
      min-width: 0;
    }
  }
`;

/**
 * Video-mode counterpart of VoiceCardsContainer: the AI card streams the
 * LiveAvatar's LiveKit video, the learner card is a live camera self-view.
 * The join/left/resume affordances mirror the voice cards.
 */
const VideoCardsContainer = () => {
  const {
    aiRolePlayId,
    aiVoiceCardState,
    learnerVoiceCardState,
    learnerMediaStream,
    voiceRecordingState,
    aiRolePlayStatus,
    isStreamError,
    handleRestartFromStreamError,
    microphoneEnabled,
  } = useContext(AiConversationContext);
  const aiRolePlay = useAiRolePlay(aiRolePlayId);
  const { aiCharacterName } = aiRolePlay ?? {};
  const { viewedSubmission } = useViewedSubmission();
  const [hasAiCharacterJoined, setHasAiCharacterJoined] = useState(false);

  useEffect(() => {
    if (voiceRecordingState !== VoiceRecordingState.RECORDING) return undefined;
    setHasAiCharacterJoined(true);
    const timerId = setTimeout(() => setHasAiCharacterJoined(false), 3000);
    return () => clearTimeout(timerId);
  }, [voiceRecordingState]);

  return (
    <div css={styles} className='d-flex flex-column'>
      <div className='d-flex justify-content-center video-cards'>
        {/* The AI card stays mounted (hidden) before the intro opens: WebRTC only
            decodes/plays media once a track has a sink, so the <video> element must
            exist from session start for the audio playout path to warm up — mounting
            it at RECORDING was measured to cold-start audio mid-utterance and cause
            the first turns' lips-lead-voice desync. */}
        <div
          className={`justify-content-center ${(voiceRecordingState === VoiceRecordingState.RECORDING
            || viewedSubmission?.closingReason === ClosingReason.TERMINATED
            || aiRolePlayStatus === AIRolePlayStatus.FAILED) ? 'd-flex' : 'd-none'}`}
        >
          <VideoCard
            role={Role.AI}
            name={aiCharacterName}
            state={aiVoiceCardState ?? VoiceCardState.IDLE}
            dataQa={config.pendo.activities.aiRolePlay.conversationModal.aiAvatarCard}
          />
        </div>
        <VideoCard
          role={Role.LEARNER}
          name={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.YOU()}
          state={learnerVoiceCardState ?? VoiceCardState.IDLE}
          mediaStream={learnerMediaStream ?? undefined}
          dataQa={config.pendo.activities.aiRolePlay.conversationModal.learnerVideoCard}
        />
      </div>
      {isStreamError && voiceRecordingState === VoiceRecordingState.RECORDING && (
        <div className='d-flex mt-4 justify-content-center'>
          <Button
            variant='primary'
            onClick={handleRestartFromStreamError}
            data-qa={config.pendo.activities.aiRolePlay.conversationModal.resumeConversation}
          >
            {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.RESUME_CONVERSATION()}
          </Button>
        </div>
      )}
      {aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS
        && voiceRecordingState === VoiceRecordingState.IDLE
        && !microphoneEnabled
        && (
        <div className='d-flex mt-6 justify-content-center'>
          <NvIcon
            icon='info'
            className='d-flex align-items-center text-black'
            size='small'
          />
          <p
            className='mb-0 text-black text-regular ml-2'
          >
            {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.CHECK_MICROPHONE()}
          </p>
        </div>
        )}
      {
        (voiceRecordingState === VoiceRecordingState.COUNTDOWN
          || (aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS
            && voiceRecordingState === VoiceRecordingState.IDLE
            && microphoneEnabled))
        && (
          <div className='d-flex mt-6 justify-content-center align-items-center'>
            <Spinner size={halfSpacing} />
            <p
              className='mb-0 text-gray-2 text-regular ml-2'
            >
              {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.WAITING_TO_JOIN(aiCharacterName)}
            </p>
          </div>
        )
      }
      {hasAiCharacterJoined && (
        <div className='d-flex mt-6 justify-content-center'>
          <p
            className='mb-0 text-black text-regular'
          >
            {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.JOINED(aiCharacterName)}
          </p>
        </div>
      )}
      {voiceRecordingState === VoiceRecordingState.COMPLETED && (
        <div className='d-flex mt-6 justify-content-center'>
          <p
            className='mb-0 text-black text-regular'
          >
            {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.LEFT(aiCharacterName)}
          </p>
        </div>
      )}
    </div>
  );
};

export default VideoCardsContainer;
